import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore } from '../store/useStore';
import { analyticsAPI, reservationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { format, subDays, eachDayOfInterval } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  CalendarCheck, 
  UserX,
  Percent
} from 'lucide-react';

const COLORS = ['#5E6AD2', '#22C55E', '#F59E0B', '#EF4444', '#14B8A6'];

export default function Analytics() {
  const { t } = useTranslation();
  const { analytics, setAnalytics } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7');
  const [dailyData, setDailyData] = useState([]);
  const [statusData, setStatusData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = subDays(endDate, parseInt(dateRange));
      
      const dateFrom = format(startDate, 'yyyy-MM-dd');
      const dateTo = format(endDate, 'yyyy-MM-dd');

      // Fetch overview
      const overviewResponse = await analyticsAPI.overview({ date_from: dateFrom, date_to: dateTo });
      setAnalytics(overviewResponse.data);

      // Fetch daily data
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      const dailyPromises = days.map(day => 
        analyticsAPI.daily(format(day, 'yyyy-MM-dd')).catch(() => ({ data: { total_reservations: 0, total_covers: 0 } }))
      );
      
      const dailyResults = await Promise.all(dailyPromises);
      const formattedDaily = days.map((day, index) => ({
        date: format(day, 'EEE dd', { locale: es }),
        fullDate: format(day, 'yyyy-MM-dd'),
        reservations: dailyResults[index]?.data?.total_reservations || 0,
        covers: dailyResults[index]?.data?.total_covers || 0,
      }));
      setDailyData(formattedDaily);

      // Prepare status data for pie chart
      const statusCounts = dailyResults.reduce((acc, result) => {
        const byStatus = result?.data?.by_status || {};
        Object.entries(byStatus).forEach(([status, count]) => {
          acc[status] = (acc[status] || 0) + count;
        });
        return acc;
      }, {});

      const formattedStatus = Object.entries(statusCounts).map(([status, count]) => ({
        name: t(`status.${status}`),
        value: count,
        status,
      }));
      setStatusData(formattedStatus);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      label: 'Total Reservaciones',
      value: analytics?.total_reservations || 0,
      icon: CalendarCheck,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      label: 'Comensales',
      value: analytics?.total_covers || 0,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: t('analytics.occupancyRate'),
      value: `${analytics?.occupancy_rate || 0}%`,
      icon: Percent,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      trend: analytics?.occupancy_rate > 50 ? 'up' : 'down',
    },
    {
      label: t('analytics.noShowRate'),
      value: `${analytics?.no_show_rate || 0}%`,
      icon: UserX,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      trend: analytics?.no_show_rate < 10 ? 'up' : 'down',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="analytics-page">
      <TopBar 
        title={t('analytics.title')} 
        helpSection="analytics"
        actions={
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32" data-testid="date-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 días</SelectItem>
              <SelectItem value="14">14 días</SelectItem>
              <SelectItem value="30">30 días</SelectItem>
              <SelectItem value="90">90 días</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <div className="p-6 space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} className="hover-lift" data-testid={`analytics-stat-${index}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-2xl font-bold">{stat.value}</p>
                      {stat.trend && (
                        stat.trend === 'up' 
                          ? <TrendingUp className="h-4 w-4 text-green-500" />
                          : <TrendingDown className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Reservations by Day Chart */}
          <Card className="lg:col-span-2" data-testid="reservations-chart">
            <CardHeader>
              <CardTitle className="text-base">{t('analytics.reservationsByDay')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      className="text-muted-foreground"
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      className="text-muted-foreground"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="reservations" 
                      name="Reservaciones"
                      fill="#5E6AD2" 
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      dataKey="covers" 
                      name="Comensales"
                      fill="#22C55E" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution Pie Chart */}
          <Card data-testid="status-chart">
            <CardHeader>
              <CardTitle className="text-base">Distribución por Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    No hay datos disponibles
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Covers Trend */}
        <Card data-testid="covers-trend-chart">
          <CardHeader>
            <CardTitle className="text-base">Tendencia de Comensales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="covers" 
                    name="Comensales"
                    stroke="#5E6AD2" 
                    strokeWidth={2}
                    dot={{ fill: '#5E6AD2', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{analytics?.average_party_size || 0}</p>
              <p className="text-sm text-muted-foreground mt-1">{t('analytics.avgPartySize')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{analytics?.confirmed_reservations || 0}</p>
              <p className="text-sm text-muted-foreground mt-1">Confirmadas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-destructive">{analytics?.cancelled || 0}</p>
              <p className="text-sm text-muted-foreground mt-1">Cancelaciones</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
